import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { FileText, Download, Loader2, Sun, Image as ImageIcon } from "lucide-react";

export default function PublicLanding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/share/${token}`);
        setData(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Link non valido o scaduto");
      }
    })();
  }, [token]);

  // Tab title = nome commessa (fallback Nureal-Operation)
  useEffect(() => {
    if (data?.commessa?.name) {
      document.title = `${data.commessa.name} – Nureal-Operation`;
    }
    return () => { document.title = "Nureal-Operation"; };
  }, [data?.commessa?.name]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 landing-bg">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-10 text-center">
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>Accesso non disponibile</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2 text-sm"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>
      </div>
    );
  }

  const pdfUrl = `${API}/public/share/${token}/pdf`;
  const moduleTitle = data.module === "sopralluogo" ? "Sopralluogo" : data.module === "intervento" ? "Intervento" : "Installazione";
  const totaleMateriali = (data.materiali || []).reduce(
    (s, m) => s + (Number(m.quantita) || 0) * Number(m.costo_unitario || 0),
    0,
  );
  const visibleSections = (data.sections || []).filter((s) => (s.notes && s.notes.trim()) || s.files.length > 0);
  const hasMateriali = (data.materiali || []).length > 0;
  const hasContent = visibleSections.length > 0 || hasMateriali;
  const totalImages = visibleSections.reduce(
    (n, s) => n + s.files.filter((f) => (f.content_type || "").startsWith("image/")).length,
    0,
  );
  const totalDocs = visibleSections.reduce(
    (n, s) => n + s.files.filter((f) => !(f.content_type || "").startsWith("image/")).length,
    0,
  );

  return (
    <div className="min-h-screen" style={{ background: "#FAFAFA" }}>
      {/* Hero */}
      <section className="landing-bg">
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-24">
          <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-6" style={{ color: "var(--primary)" }}>
            <Sun className="w-3.5 h-3.5" /> Report {moduleTitle}
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl" style={{ fontFamily: "Manrope" }} data-testid="public-header-title">
            {data.commessa?.name || "Documentazione impianto"}
          </h1>
          <p className="mt-3 text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
            Documentazione completa di <strong>{moduleTitle}</strong>.
            Puoi consultare tutte le sezioni e scaricare il PDF finale.
          </p>

          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-[color:var(--primary)] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[color:var(--primary-hover)] transition-all"
            data-testid="public-download-pdf"
          >
            <Download className="w-4 h-4" /> Scarica PDF completo
          </a>
        </div>
      </section>

      {/* Info */}
      <section className="max-w-5xl mx-auto px-6 -mt-12 relative z-10">
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8 grid md:grid-cols-3 gap-4">
          <InfoLine label="Cliente" value={`${data.cliente?.nome} ${data.cliente?.cognome_ragione_sociale}`} />
          <InfoLine label="ID pratica" value={data.cliente?.cliente_id} />
          <InfoLine label="Commessa" value={data.commessa?.name} />
          <InfoLine label="Indirizzo" value={`${data.cliente?.indirizzo || ""}`} />
          <InfoLine label="Città" value={`${data.cliente?.cap || ""} ${data.cliente?.citta || ""} (${data.cliente?.provincia || ""})`} />
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-5xl mx-auto px-6 py-16 space-y-10">
        {!hasContent && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-10 text-center" data-testid="public-empty">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "var(--surface-muted)", color: "var(--primary)" }}>
              <ImageIcon className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Manrope" }}>Contenuti in preparazione</h3>
            <p className="text-sm max-w-md mx-auto" style={{ color: "var(--text-muted)" }}>
              Il nostro team sta completando la documentazione del tuo {moduleTitle.toLowerCase()}.
              Torna su questo link tra qualche ora: troverai foto, note e il PDF finale.
            </p>
          </div>
        )}

        {hasContent && (totalImages > 0 || totalDocs > 0) && (
          <div className="flex flex-wrap gap-3 text-xs" data-testid="public-stats">
            <span className="inline-flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-full" style={{ borderColor: "var(--border)" }}>
              <ImageIcon className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> {totalImages} foto
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-full" style={{ borderColor: "var(--border)" }}>
              <FileText className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> {totalDocs} documenti
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white border px-3 py-1.5 rounded-full" style={{ borderColor: "var(--border)" }}>
              {visibleSections.length} sezioni compilate
            </span>
          </div>
        )}

        {visibleSections.map((s) => (
            <div key={s.slug} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-1 rounded-full" style={{ background: "var(--primary)" }} />
                <h2 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>{s.label}</h2>
              </div>
              {s.notes && (
                <div
                  className="text-[15px] leading-relaxed rt-notes"
                  style={{ color: "var(--text)" }}
                  dangerouslySetInnerHTML={{ __html: s.notes }}
                />
              )}
              {s.files.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                  {s.files.map((f) => {
                    const isImg = (f.content_type || "").startsWith("image/");
                    const src = `${API}/files/${f.id}/download?token=${token}`;
                    return (
                      <div key={f.id} className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--border)" }}>
                        {isImg ? (
                          <a href={src} target="_blank" rel="noreferrer" className="block group" data-testid={`public-image-${f.id}`}>
                            <img src={src} alt={f.original_filename} className="w-full h-40 object-cover transition-transform group-hover:scale-[1.02]" loading="lazy" />
                          </a>
                        ) : (
                          <a href={src} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-40 bg-[color:var(--surface-muted)]" data-testid={`public-file-${f.id}`}>
                            <FileText className="w-10 h-10" style={{ color: "var(--primary)" }} />
                            <span className="text-xs mt-1">Apri PDF</span>
                          </a>
                        )}
                        <div className="p-2 text-xs truncate">{f.original_filename}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

        {(data.materiali || []).length > 0 && (
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "Manrope" }}>Materiali installati</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ color: "var(--text-muted)" }}>
                    <th className="py-2">Categoria</th>
                    <th className="py-2">Prodotto</th>
                    <th className="py-2 text-right">Q.tà</th>
                    <th className="py-2 text-right">€ unit.</th>
                    <th className="py-2 text-right">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.materiali.map((m, i) => {
                    const tot = (Number(m.quantita) || 0) * Number(m.costo_unitario || 0);
                    return (
                      <tr key={i} className="border-t" style={{ borderColor: "var(--border)" }}>
                        <td className="py-2">{m.categoria_label}</td>
                        <td className="py-2">{m.prodotto_nome}</td>
                        <td className="py-2 text-right">{m.quantita}</td>
                        <td className="py-2 text-right">€ {Number(m.costo_unitario || 0).toFixed(2)}</td>
                        <td className="py-2 text-right font-semibold">€ {tot.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2" style={{ borderColor: "var(--primary)" }}>
                    <td colSpan="4" className="py-3 text-right font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Totale</td>
                    <td className="py-3 text-right text-xl font-extrabold" style={{ color: "var(--primary)" }}>€ {totaleMateriali.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </section>

      <footer className="border-t py-8 text-center text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
        Documento condiviso tramite Nureal-Operation · Valido solo per il destinatario autorizzato
      </footer>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--primary)" }}>{label}</div>
      <div className="mt-1 font-medium">{value || "—"}</div>
    </div>
  );
}
